import React, { useEffect, useState } from 'react';
import { Building2, User, Phone, Mail, Clock } from 'lucide-react';
import { getActiveUserInfo } from '../utils/localStore.util';
import { usePageMeta } from '../utils/pageMeta.util';
import { useSetPageHeader } from '../contexts/PageHeaderContext';

interface ContactItem {
  department: string;
  contact_person: string;
  position: string;
  phone: string;
  email: string;
  schedule: string;
}

interface ApiResponse {
  code: number;
  status: boolean;
  message: string;
  data: ContactItem[];
}

const ContactInfoPage: React.FC = () => {
  usePageMeta({
    title: 'Contact Information | HCAE Client Portal',
    description: 'Find department contact people, phone numbers, emails, and schedules in the HCAE Client Portal.',
  });
  useSetPageHeader('Contact Info', 'Department contacts and schedules');

  const user = getActiveUserInfo();
  const BASE_URL = process.env.REACT_APP_API_BASE_URL;

  const [contacts, setContacts] = useState<ContactItem[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchContacts();
  }, []);

  const fetchContacts = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${BASE_URL}contact-info/list`, {
        headers: { 'Content-Type': 'application/json', token: `${user?.api_token}` },
      });
      const data: ApiResponse = await response.json();
      setContacts(data?.data || []);
    } catch (error) {
      console.error('Contact info fetch error:', error);
    } finally {
      setLoading(false);
    }
  };

  const renderSkeleton = () => (
    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
      {[1, 2, 3, 4, 5, 6].map((i) => (
        <div key={i} className="bg-white rounded-2xl border border-brand-100 p-5 animate-pulse">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-xl bg-brand-100 shrink-0" />
            <div className="h-4 w-32 rounded-full bg-brand-100" />
          </div>
          <div className="space-y-2.5">
            <div className="h-3 w-48 rounded-full bg-slate-100" />
            <div className="h-3 w-36 rounded-full bg-slate-100" />
            <div className="h-3 w-44 rounded-full bg-slate-100" />
          </div>
        </div>
      ))}
    </div>
  );

  return (
    <div className="max-w-7xl mx-auto space-y-6">


      {loading ? (
        renderSkeleton()
      ) : contacts.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {contacts.map((contact, index) => (
            <div
              key={index}
              className="bg-white rounded-2xl border border-brand-100 p-5 hover:shadow-md hover:border-brand-200 transition-all duration-200 group"
            >
              {/* Department header */}
              <div className="flex items-center gap-3 mb-4 pb-4 border-b border-slate-100">
                <div className="w-10 h-10 rounded-xl bg-brand-50 flex items-center justify-center text-brand-600 shrink-0 group-hover:bg-brand-100 transition-colors">
                  <Building2 size={18} />
                </div>
                <h3 className="font-bold text-sm text-slate-800 leading-tight">
                  {contact.department}
                </h3>
              </div>

              {/* Contact details */}
              <div className="space-y-3">
                <div className="flex items-center gap-2.5">
                  <User size={14} className="text-brand-400 shrink-0 mt-0.5" />
                  <div>
                    <span className="text-sm font-semibold text-slate-700">
                      {contact.contact_person}
                    </span>
                    {contact.position && (
                      <span className="text-xs text-brand-400 ml-1.5">· {contact.position}</span>
                    )}
                  </div>
                </div>

                {contact.phone && (
                  <a
                    href={`tel:${contact.phone}`}
                    className="flex items-center gap-2.5 group/link"
                  >
                    <Phone size={14} className="text-brand-400 shrink-0" />
                    <span className="text-sm text-slate-600 font-medium group-hover/link:text-brand-700 transition-colors">
                      {contact.phone}
                    </span>
                  </a>
                )}

                {contact.email && (
                  <a
                    href={`mailto:${contact.email}`}
                    className="flex items-center gap-2.5 group/link"
                  >
                    <Mail size={14} className="text-brand-400 shrink-0" />
                    <span className="text-sm text-slate-600 font-medium truncate group-hover/link:text-brand-700 transition-colors">
                      {contact.email}
                    </span>
                  </a>
                )}

                {contact.schedule && (
                  <div className="flex items-center gap-2.5">
                    <Clock size={14} className="text-brand-400 shrink-0" />
                    <span className="text-sm text-slate-500 font-medium">{contact.schedule}</span>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="bg-white rounded-2xl border border-slate-100 flex flex-col items-center justify-center py-24">
          <div className="w-16 h-16 rounded-2xl bg-brand-50 flex items-center justify-center text-brand-300 mb-4">
            <Phone size={28} />
          </div>
          <p className="text-slate-500 font-semibold">No contact info found</p>
        </div>
      )}
    </div>
  );
};

export default ContactInfoPage;
